import { SceneNode } from '../core/SceneNode';
import { POIData } from './POIData';
import { Mesh3D } from '../shapes/Mesh3D';
import { ShapeFactory } from '../shapes/ShapeFactory';
import { AppSettingsData, LabelParentMode } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { LayoutData } from '../layout/LayoutData';
import * as THREE from 'three';
import { UUID, Vector3, Quaternion, TextRenderMode } from '../types';
import { Logger } from '../core/Logger';
import { getCanonicalShapeName } from '../shapes/ShapeTypes';
import { SettingsProperty } from '../properties/SettingsProperty';
import { LabelMesh, LabelMeshFactory } from '../shapes';
import { MeshPointIndicator } from '../shapes/MeshPointIndicator';
import { ConnectorShape } from '../shapes/ConnectorShape';
import { HTMLLabelMesh } from '../shapes/HTMLLabelMesh';
import { CanvasLabelMesh } from '../shapes/CanvasLabelMesh';
import { TestingConfig } from '../core/TestingConfig';
import { IPOIFieldAware, FieldConfiguration, DEFAULT_FIELD_CONFIG } from '../poi/POIFieldInterface'
import { FieldAwareness } from '../field/FieldAwareness'
import { FieldContext } from '../field/FieldContext'
import { POIAnimation } from './POIAnimation'

/**
 * POI - Data-driven Point of Interest
 * Automatically reacts to POIData changes without manual intervention
 * Pure data-first thinking: change data, POI updates automatically
 */
export class POI extends SceneNode implements IPOIFieldAware{
    // POI-specific data with animated properties
    public readonly poidata: POIData;
    
    // Additional meshes support (for manual additions)
    private _additionalMeshes: Map<UUID, Mesh3D> = new Map();
    
    // State tracking
    private _isSelected: boolean = false;
    private _isHovered: boolean = false;
    private _isActive: boolean = false;
    private _lastLogTime: number = 0;

    private _appSettings: AppSettingsData | undefined;
    private _sceneSettings: SceneSettingsData | undefined;
    private _layoutData: LayoutData | undefined;

    private _label: LabelMesh | null = null;
    private _meshPointIndicator: MeshPointIndicator | null = null;
    private _connectorShape: ConnectorShape | null = null;
    private _shape: Mesh3D | null = null;  // Cached shape reference for performance

    // Runtime-only metadata from ARTrack (not parsed from markdown)
    // Note: artrackMeta is now on POIData to keep runtime metadata alongside data properties




    // Field properties
    public index: number = 0
    public fieldAwareness: FieldAwareness | null = null
    
    // Field configuration
    private _fieldConfig: FieldConfiguration = { ...DEFAULT_FIELD_CONFIG }
    private _useFieldAwareness: boolean = false
    
    // Animation system
    private _poiAnimation: POIAnimation = new POIAnimation()
    
    // Performance tracking
    private _lastUpdateTime: number = 0
    private _updateCount: number = 0
    private _performanceMetrics: {
        averageUpdateTime: number
        maxUpdateTime: number
        minUpdateTime: number
    } = {
        averageUpdateTime: 0,
        maxUpdateTime: 0,
        minUpdateTime: Infinity
    }






    constructor(name: string = 'POI', poidata: POIData, sceneSettings?: SceneSettingsData, appSettings?: AppSettingsData, layoutData?: LayoutData) {
        super(name);

        // Initialize this node's inherited mesh as a container.
        this._mesh = new THREE.Group();
        this._mesh.name = this.name;
        this._mesh.userData.sceneNode = this;

        this._appSettings = appSettings;
        this._sceneSettings = sceneSettings;
        this._layoutData = layoutData;
        this.poidata = poidata;
        
        // The shape must be created before the animator, as the animator targets the shape.
        this.createMeshFromData();

        // Animation properties are automatically connected by the shape itself
        
        this.connectTransformToPOIData();
        
        this.syncLabelFromData();
        this.syncMeshPointIndicator();
        this.syncConnectorShape();
        
        this.poidata.showLabel.on(() => {
            ////console.log(`🎯 [${this.name}] showLabel changed to: ${this.poidata.showLabel.value}`);
            this.syncLabelFromData();
        });
        this.poidata.html.on(() => {
            ////console.log(`🎯 [${this.name}] html changed to: "${this.poidata.html.value}"`);
            this.syncLabelFromData();
        });
        this.poidata.labelRenderMode.on(() => {
            ////console.log(`🎯 [${this.name}] labelRenderMode changed to: ${this.poidata.labelRenderMode.value}`);
            this.recreateLabelWithNewType();
        });
        
        // Mesh Point Indicator listeners
        this.poidata.labelMeshPoint.on(() => this.syncMeshPointIndicator());
        this.poidata.showMeshPointNumbers.on(() => this.syncMeshPointIndicator());
        this.poidata.meshPointColor.on(() => this.syncMeshPointIndicator());
        this.poidata.meshPointBackgroundColor.on(() => this.syncMeshPointIndicator());
        this.poidata.labelNr.on(() => this.syncMeshPointIndicator());
        
        // Connector listeners
        this.poidata.showConnector.on(() => this.syncConnectorShape());
        this.poidata.connectorColor.on(() => this.syncConnectorShape());
        this.poidata.connectorThickness.on(() => this.syncConnectorShape());
        this.poidata.labelOpacity.on(() => this.syncConnectorShape());
        
        // Label parent mode listener
        this.poidata.labelParentMode.on(() => {
            if (this._label) {
                const newParent = this.getLabelParent();
                //console.log(`🔄 [${this.name}] Label parent mode changed - reattaching label to: ${newParent?.name || 'null'}`);
                this._label.setParent(newParent);
            }
        });

        // Initialize field awareness if requested
        if (this._layoutData?.fieldAwarenessEnabled.value) {
            this._useFieldAwareness = true
        }
        
        // Override update method to include field awareness
        this.setupFieldAwareUpdate()
    }



    private createMeshFromData(): void {
        ////console.log(`🏗️ POI ${this.name}: createMeshFromData called`);
        ////console.log(`🏗️ POI ${this.name}: Current shape type in poidata: ${this.poidata.shape.value}`);
        
        // Remove the old shape if it exists.
        const oldShape = this.shape;
        if (oldShape) {
            oldShape.destroy();
            this._shape = null;  // Clear cached reference
            ////console.log(`🏗️ POI ${this.name}: Destroyed old shape`);
        }

        // 🚫 Hard guard: if shape is 'none' (including aliases), do not create any mesh
        const desiredShape = this.poidata.shape.value;
        if (desiredShape && getCanonicalShapeName(desiredShape) === 'none') {
            ////console.log(`🏗️ POI ${this.name}: Shape set to 'none' → skipping mesh creation`);
            return;
        }

        // Create the new shape and attach it to this POI.
        const newShape = ShapeFactory.createShape(this, this._sceneSettings, this._appSettings);
        ////console.log(`🏗️ POI ${this.name}: ShapeFactory created: ${!!newShape} (type: ${newShape?.constructor.name})`);
        
        if (newShape) {
            newShape.setParent(this);
            this._shape = newShape;  // Cache the new shape reference
            ////console.log(`🏗️ POI ${this.name}: New shape attached as child`);
            
            // Verify the shape was attached correctly
            this.debugChildren();
            ////console.log(`🏗️ POI ${this.name}: Verification - can find shape: ${!!this.shape}`);
            if (this.shape) {
                ////console.log(`🏗️ POI ${this.name}: Verification - shape type: ${this.shape.constructor.name}`);
            }
        }

        // Animation properties are automatically connected by the new shape
        ////console.log(`🏗️ POI ${this.name}: createMeshFromData completed`);
    }

    /**
     * Handle shape change - triggered by data change
     */
    private onShapeDataChanged = (newShapeType: string | null, oldShapeType?: string | null, property?: SettingsProperty<string>) => {
        ////console.log(`🔄 POI ${this.name}: onShapeDataChanged called`);
        ////console.log(`🔄 POI ${this.name}: oldShapeType=${oldShapeType} -> newShapeType=${newShapeType}`);
        ////console.log(`🔄 POI ${this.name}: Current shape exists: ${!!this.shape}`);
        
        // Skip if no shape exists yet or if the shape type hasn't actually changed
        if (!this.shape || oldShapeType === newShapeType) {
            ////console.log(`🔄 POI ${this.name}: Exiting early - no shape or same shape type (old: ${oldShapeType}, new: ${newShapeType})`);
            return;
        }
        
        ////console.log(`🔄 POI ${this.name}: Data-driven shape change from ${oldShapeType} to ${newShapeType}`);
        
        // Re-run the creation process
        this.createMeshFromData();

        // Animation properties are automatically connected by the new shape
    }

    // Mesh management - simplified
    addMesh(mesh: Mesh3D): void {
        this._additionalMeshes.set(mesh.id, mesh);
        mesh.setParent(this);
    }

    removeMesh(meshId: UUID): boolean {
        const mesh = this._additionalMeshes.get(meshId);
        if (!mesh) return false;
        
        this._additionalMeshes.delete(meshId);
        mesh.setParent(null);
        mesh.destroy();
        return true;
    }

    getMesh(meshId: UUID): Mesh3D | undefined {
        return this._additionalMeshes.get(meshId);
    }

    getAllMeshes(): Mesh3D[] {
        const meshes: Mesh3D[] = [];
        const shape = this.shape;
        if (shape) {
            meshes.push(shape);
        }
        if (this._label) {
            meshes.push(this._label);
        }
        meshes.push(...Array.from(this._additionalMeshes.values()));
        return meshes;
    }

    getMeshCount(): number {
        return (this.shape ? 1 : 0) + (this._label ? 1 : 0) + this._additionalMeshes.size;
    }

    // Read-only access to shape
    get shape(): Mesh3D | null {
        // Fast path: Return cached shape if available
        if (this._shape) {
            return this._shape;
        }
        
        // Fallback: Search children and cache result (rare case)
        const children = this.getChildren();
        
        // Optimize for common case: single shape child
        if (children.length === 1 && children[0] instanceof Mesh3D && !(children[0] instanceof LabelMesh)) {
            this._shape = children[0];
            return this._shape;
        }
        
        // Search through children (for multiple children case)
        for (const child of children) {
            if (child instanceof Mesh3D && !(child instanceof LabelMesh)) {
                this._shape = child;  // Cache for next time
                return this._shape;
            }
        }
        
        return null;
    }

    // Debug helper to examine children
    private debugChildren(): void {
        const children = this.getChildren();
        ////console.log(`🔍 POI ${this.name}: debugChildren - total children: ${children.length}`);
        
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            ////console.log(`🔍 POI ${this.name}: child[${i}]: ${child.constructor.name}, isMesh3D: ${child instanceof Mesh3D}, isLabelMesh: ${child instanceof LabelMesh}`);
        }
    }

    // Read-only access to label
    get label(): LabelMesh | null {
        return this._label;
    }

    /**
     * Determines the correct parent for labels based on labelParentMode setting
     */
    private getLabelParent(): SceneNode | null {
        const parentMode = this.poidata.labelParentMode.value;
        
        if (parentMode === LabelParentMode.POISHAPE && this.shape) {
            return this.shape;
        }
        
        // Default to POI itself (stable position)
        return this;
    }

    // Read-only access to shape type
    getShapeType(): string | null {
        return this.poidata.shape.value;
    }

    getAppSettings(): AppSettingsData | undefined {
        return this._appSettings;
    }

    getSceneSettings(): SceneSettingsData | undefined {
        return this._sceneSettings;
    }

    getLayoutData(): LayoutData | undefined {
        return this._layoutData;
    }

    // Selection and interaction
    setSelected(selected: boolean): void {
        this._isSelected = selected;
        this.updateSelectionVisuals();
    }

    isSelected(): boolean {
        return this._isSelected;
    }

    setHovered(hovered: boolean): void {
        this._isHovered = hovered;
        this.updateHoverVisuals();
    }

    isHovered(): boolean {
        return this._isHovered;
    }

    setActive(active: boolean): void {
        if (this._isActive === active) return;
        
        this._isActive = active;
        this.updateActiveVisuals();
        
        // 🎯 Handle label visibility based on fadeLabelOnActive setting
        this.handleLabelVisibilityOnActive(active);
        
        // Call setActive/setInactive on all materials
        if (active) {
            this.activateAllMaterials();
        } else {
            this.deactivateAllMaterials();
        }
    }

    isActive(): boolean {
        return this._isActive;
    }

    /**
     * 🎯 Handle label visibility when POI becomes active/inactive
     * Works for all label types: ScreenHTML, HTML, Canvas
     */
    private handleLabelVisibilityOnActive(active: boolean): void {
        // Check if fadeLabelOnActive setting is enabled (individual POI setting with global fallback)
        const fadeLabelOnActive = this.poidata.fadeLabelOnActive.value;
        if (!fadeLabelOnActive || !this._label) return;

        if (active) {
            // POI became active - show label
            if (this._label.show) {
                this._label.show();
                //console.log(`🎯 POI ${this.name} activated - showing label`);
            }
        } else {
            // POI became inactive - hide label
            if (this._label.hide) {
                this._label.hide();
                //console.log(`🎯 POI ${this.name} deactivated - hiding label`);
            }
        }
    }

    /**
     * Establishes the link between the POI's data and its transform.
     * This is the core of the data-driven movement and appearance.
     * Position is linked directly via the hierarchical property system.
     * Rotation and Scale are converted and updated via event listeners due to type differences.
     */
    private connectTransformToPOIData(): void {
        // Position, Rotation, and Scale now all use event listeners to trigger animation.
        // This decouples them from the hierarchical property system for value propagation,
        // allowing the transform properties to properly manage their own animation state.
        
        // 1. Position: Listen for changes and trigger the handler.
        this.poidata.position.on(this.handlePositionChange);
        this.handlePositionChange(this.poidata.position.value); // Initial sync

        // 2. Rotation: Listen for changes and trigger the handler.
        this.poidata.rotation.on(this.handleRotationChange);
        this.handleRotationChange(this.poidata.rotation.value); // Initial sync

        // 3. Scale: Listen for changes and trigger the handler.
        this.poidata.scale.on(this.handleScaleChange);
        this.handleScaleChange(this.poidata.scale.value); // Initial sync

        // 4. Shape: Listen for changes and recreate the mesh.
        this.poidata.shape.on(this.onShapeDataChanged);
    }

    // Visual updates
    private updateSelectionVisuals(): void {
        // Logic to change appearance when selected, e.g., outline
    }

    private updateHoverVisuals(): void {
        // Logic to change appearance when hovered, e.g., highlight
    }

    private updateActiveVisuals(): void {
        // Logic to change appearance when active, e.g., glow effect
    }

    // Material activation control - generic for all material types
    private activateAllMaterials(): void {
        const materials = this.getAllMaterials();
        
        for (const material of materials) {
            ////console.log(`🎯 Activating material in POI: ${this.name}`);
            material.setActive();
        }

        // 🎭 ALSO: Activate shapes that have their own activation logic (like GLTFShape animations)
        if (this.shape) {
            ////console.log(`🎯 Activating shape in POI: ${this.name}`);
            this.shape.setActive();
        }
    }

    private deactivateAllMaterials(): void {
        const materials = this.getAllMaterials();
        
        for (const material of materials) {
            ////console.log(`🎯 Deactivating material in POI: ${this.name}`);
            material.setInactive();
        }

        // 🎭 ALSO: Deactivate shapes that have their own activation logic (like GLTFShape animations)
        if (this.shape) {
            ////console.log(`🎯 Deactivating shape in POI: ${this.name}`);
            this.shape.setInactive();
        }
    }

    public getAllMaterials(): any[] {
        const materials: any[] = [];
        
        // Check the main shape's materials
        if (this.shape) {
            if (this.shape.primaryMaterial) {
                materials.push(this.shape.primaryMaterial);
            }
            if (this.shape.secondaryMaterial) {
                materials.push(this.shape.secondaryMaterial);
            }
        }

        // Check additional meshes
        const allMeshes = this.getAllMeshes();
        for (const mesh of allMeshes) {
            if (mesh.primaryMaterial) {
                materials.push(mesh.primaryMaterial);
            }
            if (mesh.secondaryMaterial) {
                materials.push(mesh.secondaryMaterial);
            }
        }

        return materials;
    }

    private removeDataListeners(): void {
        // This is important for cleanup to prevent memory leaks.
        // We remove the specific listeners we added.
        this.poidata.position.off(this.handlePositionChange);
        this.poidata.rotation.off(this.handleRotationChange);
        this.poidata.scale.off(this.handleScaleChange);
        this.poidata.shape.off(this.onShapeDataChanged);
    }


      /**
   * Override onUpdate to integrate field awareness with scene graph
   */
  protected override onUpdate(deltaTime: number): void {
    super.onUpdate(deltaTime);
    // Transform sync now handled by SceneNode.syncTransformToThreeJS()
    this.updateFromField(deltaTime);
  }

    protected onDestroy(): void {
        super.onDestroy();
        
        // Clean up label
        if (this._label) {
            this._label.dispose(); // Dispose directly without making it a root node
            this._label = null;
        }
        
        // Clean up mesh point indicator
        if (this._meshPointIndicator) {
            this._meshPointIndicator.destroy(); // Destroy directly without making it a root node
            this._meshPointIndicator = null;
        }
        
        // Clean up connector shape
        if (this._connectorShape) {
            this._connectorShape.destroy(); // Destroy directly without making it a root node
            this._connectorShape = null;
        }
        
        this.removeDataListeners();
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        
        // Include all children (shape, additional meshes, labels, etc.)
        for (const child of this.getChildren()) {
            box.union(child.getBoundingBox());
        }

        return box;
    }

    // Factory methods
    static createWithDefaultShape(): POI {
        const poidata = new POIData();
        poidata.shape.value = 'box';
        return new POI('POI', poidata);
    }

    static createWithShape(name: string, shape: string): POI {
        const poidata = new POIData();
        poidata.shape.value = shape;
        return new POI(name, poidata);
    }

    static createWithMesh(name: string, mesh: Mesh3D): POI {
        const poidata = new POIData();
        const poi = new POI(name, poidata);
        poi.addMesh(mesh);
        return poi;
    }

    static createWithData(name: string, data: POIData): POI {
        return new POI(name, data);
    }

    /**
     * Create POI with default data (async because POIData.createDefault is now async)
     */
    static async createWithDefaultData(name?: string): Promise<POI> {
        const poidata = new POIData();
        const poi = new POI(name, poidata);
        return Promise.resolve(poi);
    }

    // Export POI data for external systems
    toPOIData(): any {
        return this.poidata;
    }

    public getPropertiesForUI(): SettingsProperty<any>[] {
        const poidataProps = this.poidata.getProperties();
        
        // // Clone and rename transform properties to avoid conflicts and make their source clear in the UI
        // const transformPosition = this.transform.position.clone().withName('Transform Position').withGroup('Transform (Live)');
        // const transformRotation = this.transform.rotation.clone().withName('Transform Rotation').withGroup('Transform (Live)');
        
        this.transform.position.group = 'Transform POI (Live)';
        this.transform.rotation.group = 'Transform POI (Live)';

        const props =  [
            ...poidataProps,
            this.transform.position,
            this.transform.rotation,
        ];

        const shape = this.shape;
        if (shape) {
            this.shape.transform.position.group = 'Transform MESH (Live)';
            this.shape.transform.rotation.group = 'Transform MESH (Live)';

            this.shape.transform.worldPosition.group = 'Worldpos (Live)';
            this.shape.transform.worldQuaternion.group = 'Worldpos (Live)';
            this.shape.transform.worldScale.group = 'Worldpos (Live)';
            
            props.push(
                this.shape.transform.position,
                this.shape.transform.rotation
            );

            props.push(
                this.shape.transform.worldPosition,
                this.shape.transform.worldQuaternion,
                this.shape.transform.worldScale
            );

        }

        // 🎯 ADD LABEL TRANSFORM PROPERTIES FOR DEBUGGING
        const label = this.label;
        if (label) {
            label.transform.position.group = 'Transform LABEL (Live)';
            label.transform.rotation.group = 'Transform LABEL (Live)';
            label.transform.scale.group = 'Transform LABEL (Live)';

            label.transform.worldPosition.group = 'Label Worldpos (Live) READ-ONLY';
            label.transform.worldQuaternion.group = 'Label Worldpos (Live) READ-ONLY';
            label.transform.worldScale.group = 'Label Worldpos (Live) READ-ONLY';
            
            props.push(
                label.transform.position,
                label.transform.rotation,
                label.transform.scale
            );

            // NOTE: World properties are READ-ONLY - they show computed values but changes don't affect the mesh
            // To change label position, use the local transform properties above
            props.push(
                label.transform.worldPosition,
                label.transform.worldQuaternion,
                label.transform.worldScale
            );

        }

        return props;
    }

    /**
     * Manually switch the label rendering type.
     * This will trigger the automatic recreation if the type is different.
     * @param newType - The new render type ('canvas', 'html', 'screen-html', or 'screen-html-static')
     */
    public switchLabelType(newType: 'canvas' | 'html' | 'screen-html' | 'screen-html-static'): void {
        const currentType = this.poidata.labelRenderMode.value;
        
        // Convert string to enum value
        let enumValue: TextRenderMode;
        switch (newType) {
            case 'canvas':
                enumValue = TextRenderMode.CANVAS;
                break;
            case 'html':
                enumValue = TextRenderMode.HTML;
                break;
            case 'screen-html':
                enumValue = TextRenderMode.SCREEN_HTML;
                break;
            case 'screen-html-static':
                enumValue = TextRenderMode.SCREEN_HTML_STATIC;
                break;
            default:
                enumValue = TextRenderMode.CANVAS;
        }
        
        if (currentType !== enumValue) {
            ////console.log(`🔄 [${this.name}] Manually switching label type: ${currentType} → ${enumValue}`);
            this.poidata.labelRenderMode.value = enumValue; // This will trigger the onChange handler
        } else {
            ////console.log(`ℹ️ [${this.name}] Label already using ${enumValue} rendering`);
        }
    }

    /**
     * Get the current label rendering type
     */
    public getLabelType(): 'canvas' | 'html' | 'screen-html' | 'screen-html-static' | null {
        return this._label?.getRenderType().toLowerCase() as 'canvas' | 'html' | 'screen-html' | 'screen-html-static' || null;
    }

    private syncLabelFromData(): void {
        //console.log(`🏷️ [${this.name}] syncLabelFromData called - showLabel: ${this.poidata.showLabel.value}, hasLabel: ${!!this._label}`);
        
        // Early exit if labels are disabled globally (startup config only)
        if (!TestingConfig.labels) {
            //console.log(`🏷️ [${this.name}] Labels globally disabled, exiting`);
            return;
        }

        const hasText = !!(this.poidata.html.value);
        const fadeOnActive = this.poidata.fadeLabelOnActive.value ?? this._appSettings?.fadeLabelOnActive.value ?? false;
        const baseShow = (this.poidata.showLabel.value ?? false) && hasText;
        const isActive = this._isActive === true;
        const shouldShow = fadeOnActive ? (baseShow && isActive) : baseShow;
        
        //console.log(`🏷️ [${this.name}] hasText: ${hasText}, shouldShow: ${shouldShow}`);

        // Create label if it doesn't exist yet AND showLabel is true
        if (!this._label && this.poidata.showLabel.value) {
            //console.log(`🏷️ [${this.name}] ✅ Creating initial label`);
            this._label = LabelMeshFactory.createMesh(this);
            if (this._label) {
                const labelParent = this.getLabelParent();
                //console.log(`🏷️ [${this.name}] Label created, setting parent to: ${labelParent?.name || 'null'} and initializing`);
                this._label.setParent(labelParent);
                this._label.initialize().then(() => {
                    // CSS3D labels manage their own scene placement via addToScene()
                    // Only sync non-CSS3D labels into the POI Group hierarchy
                    // CSS3D labels manage their own scene via addToScene() — don't reparent
                    const threeObj = this._label?.getThreeObjects()?.[0];
                    if (this._label && !(threeObj as any)?.element) {
                        this._label.syncThreeJsParent();
                    }
                }).catch(err => console.error('Failed to initialize label:', err));
            } else {
                //console.log(`🏷️ [${this.name}] ❌ LabelMeshFactory.createMesh returned null`);
            }
        } else if (!this.poidata.showLabel.value) {
            //console.log(`🏷️ [${this.name}] 🚫 Skipping label creation - showLabel is false`);
        }

        // Show/hide based on settings (instead of destroy/create)
        if (this._label) {
            this._label.setVisible(shouldShow);
            //console.log(`🏷️ [${this.name}] ${shouldShow ? '👁️ Showing' : '🙈 Hiding'} label`);
            
            if (shouldShow) {
                // Update content when showing
                const text = this.poidata.html.value;
                this._label.updateContent(text ?? '');
                
                // 🔧 FIX: Force label positioning when shown
                (this._label as any)._updatePosition();
                (this._label as any)._updateScale();
                (this._label as any)._updateOrientation();
            }
        }
        
        // Sync connector shape when label changes
        this.syncConnectorShape();
    }

    /**
     * Recreates the label with a new type when labelRenderMode changes.
     * This involves completely removing the old label instance and creating a new one
     * because CanvasLabelMesh and HTMLLabelMesh are different classes with different rendering systems.
     */
    private recreateLabelWithNewType(): void {
        ////console.log(`🔄 [${this.name}] recreateLabelWithNewType called`);
        
        // Early exit if labels are disabled globally (startup config only)
        if (!TestingConfig.labels) {
            ////console.log(`🔄 [${this.name}] Labels globally disabled, skipping recreation`);
            return;
        }

        // Store current label state before disposal
        const wasVisible = this._label?.visible ?? false;
        const currentText = this.poidata.html.value;
        const oldRenderType = this._label?.getRenderType();
        
        ////console.log(`🔄 [${this.name}] Current state - wasVisible: ${wasVisible}, oldType: ${oldRenderType}, newType: ${this.poidata.labelRenderMode.value}`);

        // Step 1: Dispose old label instance completely
        if (this._label) {
            //console.log(`🗑️ [${this.name}] Disposing old label instance (${oldRenderType})`);
            this._label.dispose();        // Clean up resources and remove from scene graph
            this._label = null;           // Clear reference
            //console.log(`✅ [${this.name}] Old label disposed and reference cleared`);
        }

        // Step 2: Create completely new label instance with current render mode
        //console.log(`🏭 [${this.name}] Creating NEW label instance with render mode: ${this.poidata.labelRenderMode.value}`);
        this._label = LabelMeshFactory.createMesh(this);
        
        if (this._label) {
            const newRenderType = this._label.getRenderType();
            //console.log(`✨ [${this.name}] New label created: ${oldRenderType} → ${newRenderType}`);
            
            // Step 3: Initialize and restore state
            const labelParent = this.getLabelParent();
            this._label.setParent(labelParent);
            this._label.initialize().then(() => {
                // CSS3D labels manage their own scene; only sync non-CSS3D
                const obj = this._label?.getThreeObjects()?.[0];
                if (this._label && !(obj as any)?.element) {
                    this._label.syncThreeJsParent();
                }
                if (this._label && wasVisible && currentText) {
                    this._label.updateContent(currentText);
                    this._label.setVisible(wasVisible);
                }
                this.syncConnectorShape();
            }).catch(err => {
                console.error(`❌ [${this.name}] Failed to initialize recreated label:`, err);
            });
        } else {
            console.error(`❌ [${this.name}] Failed to create new label instance`);
        }
    }

    /**
     * Manages the mesh point indicator visibility and updates
     */
    private syncMeshPointIndicator(): void {
        // Early exit if labels are disabled globally OR mesh points are disabled specifically
        if (!TestingConfig.labels || !TestingConfig.labelMeshPoints) {
            if (this._meshPointIndicator) {
                this._meshPointIndicator.destroy(); // Destroy directly without making it a root node
                this._meshPointIndicator = null;
            }
            return;
        }

        const meshPoint = this.poidata.labelMeshPoint.value;
        const shouldShow = meshPoint !== null;
        
        if (shouldShow && !this._meshPointIndicator) {
            // Create mesh point indicator
            this._meshPointIndicator = new MeshPointIndicator(this);
            if (this.shape) {
                this._meshPointIndicator.setParent(this.shape); // Part of shape so it rotates with shape
            } else {
                this._meshPointIndicator.setParent(this);
            }
        } else if (!shouldShow && this._meshPointIndicator) {
            // Remove mesh point indicator
            this._meshPointIndicator.destroy(); // Destroy directly without making it a root node
            this._meshPointIndicator = null;
        }
        
        // Update indicator if it exists
        if (this._meshPointIndicator) {
            this._meshPointIndicator.triggerUpdate();
        }
    }

    /**
     * Manages the connector shape visibility and updates
     */
    private syncConnectorShape(): void {
        // Early exit if labels are disabled globally
        if (!TestingConfig.labels) {
            if (this._connectorShape) {
                this._connectorShape.destroy(); // Destroy directly without making it a root node
                this._connectorShape = null;
            }
            return;
        }

        // Respect both global and POI-specific connector flags (TestingConfig.labelConnectors no longer hard-gates)
        const globalAllow = this._appSettings?.showConnector.value ?? true;
        const showConnector = this.poidata.showConnector.value && globalAllow;
        const hasLabel = this._label !== null;
        const shouldShow = showConnector !== null && showConnector && hasLabel;
        
        if (shouldShow && !this._connectorShape && this._label) {
            // Create connector shape
            this._connectorShape = new ConnectorShape(this, this._label, this._sceneSettings, this._appSettings);
            if (this.shape) {
                this._connectorShape.setParent(this.shape); // Part of shape so it rotates with shape
            } else {
                this._connectorShape.setParent(this);
            }
        } else if (!shouldShow && this._connectorShape) {
            // Remove connector shape
            this._connectorShape.destroy(); // Destroy directly without making it a root node
            this._connectorShape = null;
        }
        
        // Update connector target if it exists
        if (this._connectorShape && this._label) {
            this._connectorShape.setTargetLabel(this._label);
        }
    }

    // Bound event handlers to ensure `this`
    private handlePositionChange = (positionVec: Vector3 | null, _oldValue?: Vector3 | null, property?: SettingsProperty<Vector3>) => {
        const isInitialSync = property === undefined;
        if (isInitialSync) {
            this.transform.position.setImmediate(positionVec);
        } else {
            this.transform.position.value = positionVec;
        }
    };

    private handleRotationChange = (rotationVec: Vector3 | null, _oldValue?: Vector3 | null, property?: SettingsProperty<Vector3>) => {
        const isInitialSync = property === undefined;
        if (rotationVec) {
            const euler = new THREE.Euler(
                THREE.MathUtils.degToRad(rotationVec.x),
                THREE.MathUtils.degToRad(rotationVec.y),
                THREE.MathUtils.degToRad(rotationVec.z),
                'YXZ'
            );
            const newRotation = new Quaternion().setFromEuler(euler);
            if (isInitialSync) {
                this.transform.rotation.setImmediate(newRotation);
            } else {
            this.transform.rotation.value = newRotation;
            }
        } else if (isInitialSync) {
            this.transform.rotation.setImmediate(null);
        }
    };

    private handleScaleChange = (scaleValue: number | null, _oldValue?: number | null, property?: SettingsProperty<number>) => {
        const isInitialSync = property === undefined;
        const scale = scaleValue ?? 1.0;

        // 🐛 DEBUG: Log all scale changes
        //console.log(`🔍 SCALE CHANGE for POI ${this.name}: ${_oldValue?.toFixed(4)} → ${scale.toFixed(4)} (isInitialSync: ${isInitialSync})`);

        // The POI node itself should not be scaled.
        // We set its scale property to 1, 1, 1.
        if (isInitialSync) {
            this.transform.scale.setImmediate(new Vector3(1, 1, 1));
        } else {
            this.transform.scale.value = new Vector3(1, 1, 1);
        }

        // The scale from the data should be applied to the child shape.
        if (this.shape) {
            const newShapeScale = new Vector3(scale, scale, scale);
            if (isInitialSync) {
                this.shape.transform.scale.setImmediate(newShapeScale);
            } else {
                this.shape.transform.scale.value = newShapeScale;
            }
            
            // DEBUG: Verify shape positioning for label alignment
            const shapeLocalPos = this.shape.transform.position.value;
            if (shapeLocalPos && !shapeLocalPos.equals(Vector3.zero())) {
                console.warn(`⚠️ [${this.name}] Shape has non-zero local position: ${shapeLocalPos.toString()} - this may cause label misalignment!`);
                
                // POTENTIAL FIX: Force shape to be centered in POI
                ////console.log(`🔧 [${this.name}] Centering shape within POI...`);
                if (isInitialSync) {
                    this.shape.transform.position.setImmediate(Vector3.zero());
                } else {
                    this.shape.transform.position.value = Vector3.zero();
                }
            }
        }
    };






// ==================== FIELD AWARENESS INTERFACE ====================
  
  /**
   * Main field update method - called every frame
   * POI checks global field awareness setting and self-manages
   */
  public updateFromField(deltaTime: number): void {
    // Check if field awareness is globally enabled
    const globalFieldAwarenessEnabled = this.getGlobalFieldAwarenessEnabled()
    
    // // DEBUG: Log field awareness detection for POI 0 only to avoid spam
    // if (this.index === 0) {
    //   const now = Date.now()
    //   if (now - this._lastLogTime > 2000) { // Log every 2 seconds
    //     //console.log(`🌪️ POI ${this.index} Field Check: global=${globalFieldAwarenessEnabled}, local=${this._useFieldAwareness}`)
    //     this._lastLogTime = now
    //   }
    // }
    
    // Self-manage field awareness based on global setting
    if (globalFieldAwarenessEnabled && !this._useFieldAwareness) {
      // Global setting is on but POI is not field-aware - enable it
      const fieldContext = this.getGlobalFieldContext()
      if (fieldContext) {
        //console.log(`🌪️ POI ${this.index} enabling field awareness - got global context`)
        this.enableFieldAwareness(fieldContext)
      } else {
        //console.log(`🌪️ POI ${this.index} cannot enable field awareness - no global context`)
      }
    } else if (!globalFieldAwarenessEnabled && this._useFieldAwareness) {
      // Global setting is off but POI is field-aware - disable it
      //console.log(`🌪️ POI ${this.index} disabling field awareness`)
      this.disableFieldAwareness()
      return
    }
    
    // Continue with normal field update if field awareness is active
    if (!this._useFieldAwareness || !this.fieldAwareness) {
      return
    }
    
    const startTime = performance.now()
    
    try {
      // Update field awareness
      this.fieldAwareness.update(this, deltaTime)
      
      // Update animation timing
      this._poiAnimation.updateTiming(deltaTime)
      
      // Call custom update callback if configured
      if (this._fieldConfig.onUpdate) {
        this._fieldConfig.onUpdate(this, deltaTime)
      }
      
      // Update performance metrics
      this.updatePerformanceMetrics(performance.now() - startTime)
      
    } catch (error) {
      console.error('Field awareness update error:', error)
      // Graceful degradation - disable field awareness on error
      this.disableFieldAwareness()
    }
  }
  
  /**
   * Set field context for this POI
   */
  public setFieldContext(context: FieldContext): void {
    if (this.fieldAwareness) {
      this.fieldAwareness.updateContext(context)
      
      // Call field change callback if configured
      if (this._fieldConfig.onFieldChange) {
        this._fieldConfig.onFieldChange(this.fieldAwareness.getContext(), context)
      }
    }
  }
  
  /**
   * Enable field awareness for this POI
   */
  public enableFieldAwareness(context: FieldContext): void {
    if (!this._useFieldAwareness) {
      this._useFieldAwareness = true
      this.fieldAwareness = new FieldAwareness(context)
      
      // Reset performance metrics
      this.resetPerformanceMetrics()
      
      // Setup field-aware update loop
      this.setupFieldAwareUpdate()
      
      //console.log(`Field Awareness ENABLED for POI ${this.id} at index ${this.index}`)
    }
  }
  
  /**
   * Disable field awareness for this POI
   */
  public disableFieldAwareness(): void {
    if (this._useFieldAwareness) {
      this._useFieldAwareness = false
      this.fieldAwareness = null
      
      // Reset to original position/rotation/scale
      this.resetToOriginalTransform()
      
      //console.log(`Field Awareness DISABLED for POI ${this.id}`)
    }
  }
  
  /**
   * Check if field awareness is enabled
   */
  public isFieldAware(): boolean {
    return this._useFieldAwareness && this.fieldAwareness !== null
  }
  
  /**
   * Get global field awareness setting - simple and direct
   */
  private getGlobalFieldAwarenessEnabled(): boolean {
    // Check both layoutData and global FieldContext state
    const fromLayoutData = this.getLayoutData()?.fieldAwarenessEnabled?.value ?? false
    const fromGlobalContext = FieldContext.getGlobalFieldAwarenessEnabled()
    return fromLayoutData || fromGlobalContext
  }
  
  /**
   * Get global field context
   */
  private getGlobalFieldContext(): FieldContext | null {
    return FieldContext.getGlobalContext()
  }
  
  /**
   * Get field index
   */
  public getFieldIndex(): number {
    return this.index
  }
  
  /**
   * Set field index
   */
  public setFieldIndex(index: number): void {
    this.index = index
  }
  
  // ==================== FIELD CONFIGURATION ====================
  
  /**
   * Update field configuration
   */
  public updateFieldConfig(config: Partial<FieldConfiguration>): void {
    this._fieldConfig = { ...this._fieldConfig, ...config }
    
    // Apply configuration changes
    if (config.updateFrequency !== undefined) {
      this.setupFieldAwareUpdate()
    }
  }
  
  /**
   * Get field configuration
   */
  public getFieldConfig(): FieldConfiguration {
    return { ...this._fieldConfig }
  }
  
  // ==================== FIELD AWARE UPDATE SYSTEM ====================
  
  /**
   * Setup field-aware update loop
   */
  private setupFieldAwareUpdate(): void {
    // Override the existing update method to include field awareness
    const originalUpdate = this.update?.bind(this)
    
    this.update = (deltaTime: number) => {
      // Call original update first
      if (originalUpdate) {
        originalUpdate(deltaTime)
      }
      
      // Then update field awareness
      if (this._useFieldAwareness && this.shouldUpdateField()) {
        this.updateFromField(deltaTime)
      }
    }
  }
  
  /**
   * Check if field should update (for performance optimization)
   */
  private shouldUpdateField(): boolean {
    // Check update frequency
    const timeSinceLastUpdate = performance.now() - this._lastUpdateTime
    const updateInterval = 1000 / this._fieldConfig.updateFrequency
    
    if (timeSinceLastUpdate < updateInterval) {
      return false
    }
    
    // Check distance-based culling
    if (this._fieldConfig.maxUpdateDistance > 0) {
      // This would need camera position - simplified for now
      // const distance = this.getDistanceToCamera()
      // if (distance > this._fieldConfig.maxUpdateDistance) {
      //   return false
      // }
    }
    
    // Check interaction state
    if (this._fieldConfig.pauseOnHover && this.isHovered()) {
      return false
    }
    
    this._lastUpdateTime = performance.now()
    return true
  }
  
  // ==================== ANIMATION PRESETS ====================
  
  /**
   * Apply breathing animation preset
   */
  public applyBreathingAnimation(): void {
    if (this.fieldAwareness) {
      this._poiAnimation.applyBreathingPreset(this, this.fieldAwareness.getContext().parameters)
    }
  }
  
  /**
   * Apply flowing animation preset
   */
  public applyFlowingAnimation(): void {
    if (this.fieldAwareness) {
      this._poiAnimation.applyFlowingPreset(this, this.fieldAwareness.getContext().parameters)
    }
  }
  
  /**
   * Apply rippling animation preset
   */
  public applyRipplingAnimation(): void {
    if (this.fieldAwareness) {
      this._poiAnimation.applyRipplingPreset(this, this.fieldAwareness.getContext().parameters)
    }
  }
  
  // ==================== PERFORMANCE MONITORING ====================
  
  /**
   * Update performance metrics
   */
  private updatePerformanceMetrics(updateTime: number): void {
    this._updateCount++
    
    // Update average
    this._performanceMetrics.averageUpdateTime = 
      (this._performanceMetrics.averageUpdateTime * (this._updateCount - 1) + updateTime) / this._updateCount
    
    // Update max
    this._performanceMetrics.maxUpdateTime = Math.max(this._performanceMetrics.maxUpdateTime, updateTime)
    
    // Update min
    this._performanceMetrics.minUpdateTime = Math.min(this._performanceMetrics.minUpdateTime, updateTime)
  }
  
  /**
   * Reset performance metrics
   */
  private resetPerformanceMetrics(): void {
    this._updateCount = 0
    this._performanceMetrics = {
      averageUpdateTime: 0,
      maxUpdateTime: 0,
      minUpdateTime: Infinity
    }
  }
  
  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): typeof this._performanceMetrics {
    return { ...this._performanceMetrics }
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Reset to original transform (before field awareness)
   */
  private resetToOriginalTransform(): void {
    // Reset position, rotation, scale to original values
    // This would ideally use stored original values
    if (this.transform) {
      this.transform.position.value?.set(0, 0, 0)
      this.transform.rotation.value?.set(0, 0, 0, 1)
      this.transform.scale.value?.set(1, 1, 1)
    }
  }
  
  /**
   * Get distance to another POI
   */
  public getDistanceToOther(other: POI): number {
    if (!this.transform || !other.transform) return Infinity
    
    const pos1 = this.transform.position.value
    const pos2 = other.transform.position.value
    
    if (!pos1 || !pos2) return Infinity
    
    return pos1.distance(pos2)
  }
  
  /**
   * Check if POI is within field bounds
   */
  public isInFieldBounds(context: FieldContext): boolean {
    if (!this.transform) return false
    
    const bounds = context.calculateFieldBounds()
    const pos = this.transform.position.value
    
    if (!pos) return false
    
    return pos.x >= bounds.min.x && pos.x <= bounds.max.x &&
           pos.y >= bounds.min.y && pos.y <= bounds.max.y &&
           pos.z >= bounds.min.z && pos.z <= bounds.max.z
  }
  
  // ==================== DEBUGGING ====================
  
  /**
   * Get debug information
   */
  public getDebugInfo(): any {
    return {
      id: this.id,
      index: this.index,
      fieldAware: this.isFieldAware(),
      updateCount: this._updateCount,
      performance: this.getPerformanceMetrics(),
      animationState: this._poiAnimation.getAnimationState(),
      fieldConfig: this._fieldConfig
    }
  }
  
  /**
   * Log field awareness status
   */
  public logFieldStatus(): void {
    //console.log(`POI ${this.id} Field Status:`, {
      //index: this.index,
      //fieldAware: this.isFieldAware(),
      //animationState: this._poiAnimation.getAnimationState(),
      //updateCount: this._updateCount,
      //performance: this.getPerformanceMetrics()
    //})
  }
  
  // ==================== FACTORY METHODS ====================
  
  /**
   * Create field-aware POI
   */
  static createFieldAware(name: string, context: FieldContext, index: number = 0): POI {
    const poidata = new POIData();
    const poi = new POI(name, poidata);
    poi.setFieldIndex(index);
    poi.enableFieldAwareness(context);
    return poi;
  }
  
  /**
   * Create field-aware POI with preset animation
   */
  static createWithAnimation(
    name: string, 
    context: FieldContext, 
    index: number, 
    animationType: 'breathing' | 'flowing' | 'rippling'
  ): POI {
    const poi = POI.createFieldAware(name, context, index)
    
    switch (animationType) {
      case 'breathing':
        poi.applyBreathingAnimation()
        break
      case 'flowing':
        poi.applyFlowingAnimation()
        break
      case 'rippling':
        poi.applyRipplingAnimation()
        break
    }
    
    return poi
  }
  


}
